import {
  X,
  User,
  Star,
  Calendar,
  FileText,
  CheckCircle2,
  Clock,
  Edit,
  Trash2,
  Send,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Review } from '../../types';
import {
  getReviewStatusColor, getReviewPeriodLabel 
} from '../../types';

interface ReviewDetailModalProps {
  readonly review: Review | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onEdit: (review: Review) => void;
  readonly onDelete: (review: Review) => void;
  readonly onSubmitForApproval: (review: Review) => void;
  readonly onApprove: (review: Review) => void;
}

const STAR_POSITIONS = ['first', 'second', 'third', 'fourth', 'fifth'] as const;

export function ReviewDetailModal({
  review,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onSubmitForApproval,
  onApprove,
}: ReviewDetailModalProps) {
  if (!isOpen || !review) return null;

  const statusColor = getReviewStatusColor(review.status);
  const periodLabel = getReviewPeriodLabel(review.period);
  
  const dueDate = new Date(review.dueDate);
  const isOverdue = review.status !== 'completed' && dueDate < new Date();
  
  const formattedDueDate = dueDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const periodRange = `${new Date(review.periodStart).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })} - ${new Date(review.periodEnd).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })}`;

  const completedDate = review.completedAt
    ? new Date(review.completedAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
    : null;

  const canSubmitForApproval = review.status === 'in-progress';
  const canApprove = review.status === 'pending-approval';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        aria-label="Close modal"
      />
      <div className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
          <h2 className="text-lg font-semibold">Performance Review Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <User className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{review.employeeName}</h3>
                <p className="text-gray-500">{review.employeeRole}</p>
                <p className="text-sm text-gray-400">{review.siteName}</p>
              </div>
            </div>
            <span className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${statusColor}`}>
              {review.status.replace('-', ' ')}
            </span>
          </div>

          <div className="border-t pt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Review Period</p>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{periodLabel}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">{periodRange}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Reviewer</p>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{review.reviewerName}</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Due Date</p>
              <div className={`flex items-center gap-2 ${isOverdue ? 'text-red-600' : ''}`}>
                {isOverdue ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <Clock className="h-4 w-4 text-gray-400" />
                )}
                <span className={isOverdue ? 'text-red-600' : 'text-gray-900'}>
                  {formattedDueDate}
                  {isOverdue && ' (Overdue)'}
                </span>
              </div>
            </div>
            {completedDate && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Completed</p>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-gray-900">{completedDate}</span>
                </div>
              </div>
            )}
          </div>

          {review.overallRating !== undefined && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Overall Rating</p>
              <div className="flex items-center gap-1">
                {STAR_POSITIONS.map((position, starIndex) => (
                  <Star
                    key={`star-detail-${review.reviewId}-${position}`}
                    className={`h-6 w-6 ${
                      starIndex < (review.overallRating ?? 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className="ml-2 text-lg font-medium text-gray-700">
                  {review.overallRating}/5
                </span>
              </div>
            </div>
          )}

          {review.strengths && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <p className="text-sm font-medium text-gray-500">Strengths</p>
              </div>
              <p className="text-gray-600 whitespace-pre-wrap">{review.strengths}</p>
            </div>
          )}

          {review.areasForImprovement && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <p className="text-sm font-medium text-gray-500">Areas for Improvement</p>
              </div>
              <p className="text-gray-600 whitespace-pre-wrap">{review.areasForImprovement}</p>
            </div>
          )}

          {review.achievements && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                <p className="text-sm font-medium text-gray-500">Key Achievements</p>
              </div>
              <p className="text-gray-600 whitespace-pre-wrap">{review.achievements}</p>
            </div>
          )}

          {review.developmentPlan && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-purple-500" />
                <p className="text-sm font-medium text-gray-500">Development Plan</p>
              </div>
              <p className="text-gray-600 whitespace-pre-wrap">{review.developmentPlan}</p>
            </div>
          )}

          {review.employeeComments && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-gray-500" />
                <p className="text-sm font-medium text-gray-500">Employee Comments</p>
              </div>
              <p className="text-gray-600 whitespace-pre-wrap">{review.employeeComments}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex items-center justify-between border-t bg-gray-50 px-6 py-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(review)}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onDelete(review)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {canSubmitForApproval && (
              <Button
                size="sm"
                onClick={() => onSubmitForApproval(review)}
              >
                <Send className="mr-2 h-4 w-4" />
                Submit for Approval
              </Button>
            )}
            {canApprove && (
              <Button
                size="sm"
                onClick={() => onApprove(review)}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
